import { Observable, Subject } from 'rxjs';
import { Component, ElementRef, inject, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Store } from '@ngxs/store';
import {
  OrganizationState,
  Organization,
  OrganizationActions,
  AdministratorRequest,
  AccountState,
  ActivityActions,
  ActivityState,
  ChildOrganization,
  ChildActivity,
} from '@app/_store';
import { takeUntil } from 'rxjs/operators';
import { Navigate } from '@ngxs/router-plugin';
import { friendlyDateTimeShort } from '@app/_helpers';
import { hasActionsExecuting } from '@ngxs-labs/actions-executing';
import { environment } from '@environments/environment';
import { NgIf, NgFor, NgClass, AsyncPipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { CurrentActivitiesComponent } from '../activities/current-activities/current-activities.component';
import { ActivityItemCardComponent } from '../activities/activity-item-card/activity-item-card.component';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-organization-overview',
  templateUrl: './organization-overview.component.html',
  styleUrls: ['./organization-overview.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [NgIf, RouterLink, NgFor, CurrentActivitiesComponent, NgClass, ActivityItemCardComponent, FormsModule, AsyncPipe],
})
export class OrganizationOverviewComponent implements OnInit, OnDestroy {
  componentDestroyed$: Subject<boolean> = new Subject<boolean>();

  currentOrganization$: Observable<Organization> = inject(Store).select(OrganizationState.current);

  showHiddenAdminFeatures$: Observable<boolean> = inject(Store).select(AccountState.showHiddenAdminFeatures);

  // Activity import is a development tool (bring a production export into dev); the API refuses it in production
  isProduction = environment.production;

  isExportingOrImporting$: Observable<boolean> = inject(Store).select(ActivityState.isExportingOrImporting);

  isHandlingAdminRequest$: Observable<boolean> = inject(Store).select(
    hasActionsExecuting([OrganizationActions.HandleAdministratorRequest])
  );

  isLoadingActivities$: Observable<boolean> = inject(Store).select(
    hasActionsExecuting([OrganizationActions.View, ActivityActions.Init])
  );

  @ViewChild('fileSelector')
  fileSelector: ElementRef;

  friendlyDateTimeShort = friendlyDateTimeShort;

  now: Date;

  constructor(private store: Store) {}

  ngOnInit(): void {
    this.now = new Date();
  }

  handleAdministratorRequest(user: AdministratorRequest, approve: boolean) {
    this.store.dispatch(new OrganizationActions.HandleAdministratorRequest(user, approve));
  }

  importActivity(event: Event): void {
    const fileList: FileList = (event.target as HTMLInputElement).files;

    const file: File = fileList[0];
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);

    this.store.dispatch(new ActivityActions.Import(formData)).pipe(takeUntil(this.componentDestroyed$)).subscribe();
  }

  postponeImportReminder() {
    this.store.dispatch(new OrganizationActions.ClearImportSuggestion(true));
  }

  viewOrganization(childOrganization: ChildOrganization) {
    this.store.dispatch(new Navigate(['organization', childOrganization.id], null, { onSameUrlNavigation: 'reload' }));
  }

  viewActivity(childOrganization: ChildOrganization, activity: ChildActivity) {
    this.store.dispatch(
      new Navigate(['organization', childOrganization.id, 'activities', activity.id], null, { onSameUrlNavigation: 'reload' })
    );
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
}
